import React, { useEffect, useState, useCallback } from "react";
import { http } from "../lib/api";
import { Plus, PencilSimple, Trash, X, Play, Prohibit, BookmarkSimple, FloppyDisk } from "@phosphor-icons/react";

const EMPTY_FORM = {
  title: "",
  department: "",
  location: "Riyadh",
  hiringManager: "",
  openings: 1,
  description: "",
  qualifications: "",
};

export default function Requisitions({ onChanged, preselectedRequisitionId, onPreselectConsumed }) {
  const [drafts, setDrafts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null); // null = create, otherwise draft doc
  const [busyRow, setBusyRow] = useState(null);
  const [flash, setFlash] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await http.get("/requisitions");
      setDrafts(r.data.drafts || []);
      if (typeof onChanged === "function") onChanged();
    } catch (e) {
      setFlash({ kind: "error", msg: `Failed to load: ${e.message}` });
    } finally { setLoading(false); }
  }, [onChanged]);

  useEffect(() => { load(); }, [load]);

  // Deep-link handoff from HR chat: open the edit modal for the matching draft.
  useEffect(() => {
    if (!preselectedRequisitionId || loading) return;
    const match = drafts.find((r) => r.id === preselectedRequisitionId);
    if (match) {
      setEditing(match);
      setModalOpen(true);
      setFlash({ kind: "ok", msg: `Opened "${match.title}" from HR chat.` });
    } else {
      setFlash({
        kind: "error",
        msg: `Requisition id "${preselectedRequisitionId}" is not a local draft — it may be a published or archived record.`,
      });
    }
    if (typeof onPreselectConsumed === "function") onPreselectConsumed();
  }, [preselectedRequisitionId, drafts, loading, onPreselectConsumed]);

  const openCreate = () => { setEditing(null); setModalOpen(true); };
  const openEdit = (row) => { setEditing(row); setModalOpen(true); };

  const remove = async (row) => {
    if (!window.confirm(`Delete draft "${row.title}"? This cannot be undone.`)) return;
    setBusyRow(row.id);
    try {
      await http.delete(`/requisitions/${row.id}`);
      setFlash({ kind: "ok", msg: `Draft "${row.title}" deleted.` });
      await load();
    } catch (e) {
      setFlash({ kind: "error", msg: e.response?.data?.detail || e.message });
    } finally { setBusyRow(null); }
  };

  const transition = async (row, state) => {
    const verb = state === "Open" ? "open" : state === "Closed" ? "close" : "reopen";
    if (state === "Closed" && !window.confirm(`Close "${row.title}"? Closed requisitions are hidden from all dropdowns and candidate flows.`)) return;
    setBusyRow(row.id);
    try {
      const r = await http.post(`/requisitions/${row.id}/state`, { state });
      setFlash({ kind: "ok", msg: `"${r.data.title}" is now ${state}.` });
      await load();
    } catch (e) {
      setFlash({ kind: "error", msg: e.response?.data?.detail || e.message });
    } finally { setBusyRow(null); }
  };

  return (
    <div className="space-y-5" data-testid="requisitions-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="label-eyebrow">Vaku · Requisitions</div>
          <h1 className="font-display text-3xl font-bold tracking-tight mt-1">
            Requisition Management
          </h1>
          <p className="text-slate-500 mt-1 text-sm">
            Track requisitions locally, from Draft to Open to Closed.
          </p>
        </div>
        <button
          data-testid="new-requisition-btn"
          className="btn btn-primary"
          onClick={openCreate}
        >
          <Plus size={16} /> New Requisition
        </button>
      </div>

      {flash && (
        <div
          className={`card p-3 text-sm flex items-start gap-2 ${flash.kind === "error" ? "!border-red-300 !bg-red-50 text-red-800" : "!border-emerald-300 !bg-emerald-50 text-emerald-800"}`}
          data-testid="requisitions-flash"
        >
          <span className="flex-1">{flash.msg}</span>
          <button className="btn-ghost p-0" onClick={() => setFlash(null)}><X size={14} /></button>
        </div>
      )}

      {/* Local requisitions */}
      <div className="card overflow-hidden">
        <div className="px-4 pt-4 pb-2 flex items-center justify-between">
          <div>
            <div className="label-eyebrow">Requisitions</div>
            <div className="text-sm text-slate-500">
              Draft → Open makes it visible in AI Interviews, Bulk CV Eval, and the sidebar filter.
            </div>
          </div>
          <span className="pill">{drafts.length}</span>
        </div>
        <table className="table">
          <thead>
            <tr>
              <th>Req #</th>
              <th>Title</th>
              <th>State</th>
              <th>Department</th>
              <th>Location</th>
              <th>Openings</th>
              <th>Updated</th>
              <th style={{ width: 260 }}></th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={8} className="text-center text-slate-400 py-6">Loading…</td></tr>
            )}
            {!loading && drafts.length === 0 && (
              <tr><td colSpan={8} className="text-center text-slate-400 py-6" data-testid="drafts-empty">
                No requisitions yet. Click <em>New Requisition</em> to create one.
              </td></tr>
            )}
            {drafts.map((r) => {
              const s = r.state || "Draft";
              const pillCls = s === "Open" ? "bg-emerald-100 text-emerald-800"
                : s === "Closed" ? "bg-slate-200 text-slate-600"
                : s === "Published" ? "bg-indigo-100 text-indigo-800"
                : "bg-amber-100 text-amber-800";
              return (
              <tr key={r.id} data-testid={`draft-row-${r.id}`}>
                <td className="font-mono text-[11px] text-slate-500">{r.requisitionNumber || "—"}</td>
                <td className="font-medium">{r.title}</td>
                <td><span className={`inline-block px-2 py-0.5 rounded text-[10px] font-medium ${pillCls}`} data-testid={`draft-state-${r.id}`}>{s}</span></td>
                <td>{r.department || "—"}</td>
                <td>{r.location || "—"}</td>
                <td className="font-mono text-xs">{r.openings ?? 1}</td>
                <td className="font-mono text-xs text-slate-500">{r.updatedAt ? new Date(r.updatedAt).toLocaleDateString() : "—"}</td>
                <td>
                  <div className="flex gap-1 justify-end flex-wrap">
                    {s === "Draft" && (
                      <button
                        className="btn btn-primary !py-1 !px-2 !text-xs !bg-emerald-700 hover:!bg-emerald-800"
                        onClick={() => transition(r, "Open")}
                        disabled={busyRow === r.id}
                        data-testid={`draft-open-${r.id}`}
                      ><Play size={12} weight="fill" /> Open</button>
                    )}
                    {s === "Closed" && (
                      <button
                        className="btn btn-outline !py-1 !px-2 !text-xs"
                        onClick={() => transition(r, "Open")}
                        disabled={busyRow === r.id}
                        data-testid={`draft-reopen-${r.id}`}
                      ><Play size={12} /> Reopen</button>
                    )}
                    {s === "Open" && (
                      <button
                        className="btn btn-outline !py-1 !px-2 !text-xs !text-slate-700"
                        onClick={() => transition(r, "Closed")}
                        disabled={busyRow === r.id}
                        data-testid={`draft-close-${r.id}`}
                      ><Prohibit size={12} /> Close</button>
                    )}
                    <button
                      className="btn btn-outline !py-1 !px-2 !text-xs"
                      onClick={() => openEdit(r)}
                      disabled={busyRow === r.id}
                      data-testid={`draft-edit-${r.id}`}
                    ><PencilSimple size={12} /> Edit</button>
                    <button
                      className="btn btn-outline !py-1 !px-2 !text-xs !text-red-700 !border-red-300 hover:!bg-red-50"
                      onClick={() => remove(r)}
                      disabled={busyRow === r.id}
                      data-testid={`draft-delete-${r.id}`}
                    ><Trash size={12} /></button>
                  </div>
                </td>
              </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Published-in-Fusion table intentionally removed — Vaku manages
          requisitions locally. */}

      {modalOpen && (
        <RequisitionModal
          initial={editing}
          onClose={() => setModalOpen(false)}
          onSaved={async () => {
            setModalOpen(false);
            setFlash({ kind: "ok", msg: editing ? "Draft updated." : "Draft created." });
            await load();
          }}
        />
      )}
    </div>
  );
}

function RequisitionModal({ initial, onClose, onSaved }) {
  const isEdit = !!initial;
  const [form, setForm] = useState(() => initial
    ? {
        title: initial.title || "",
        department: initial.department || "",
        location: initial.location || "Riyadh",
        hiringManager: initial.hiringManager || "",
        openings: initial.openings ?? 1,
        description: initial.description || "",
        qualifications: initial.qualifications || "",
      }
    : { ...EMPTY_FORM });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  // Templates state
  const [templates, setTemplates] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState("");
  const [showSaveTpl, setShowSaveTpl] = useState(false);
  const [tplName, setTplName] = useState("");
  const [tplBusy, setTplBusy] = useState(false);
  const [tplFlash, setTplFlash] = useState(null);

  const loadTemplates = useCallback(async () => {
    try {
      const r = await http.get("/requisition-templates");
      setTemplates(r.data || []);
    } catch (_e) {
      /* templates are optional; ignore load failure */
    }
  }, []);

  useEffect(() => { loadTemplates(); }, [loadTemplates]);

  const applyTemplate = (tplId) => {
    setSelectedTemplateId(tplId);
    if (!tplId) return;
    const t = templates.find((x) => x.id === tplId);
    if (!t) return;
    setForm({
      title: t.title || "",
      department: t.department || "",
      location: t.location || "Riyadh",
      hiringManager: t.hiringManager || "",
      openings: t.openings || 1,
      description: t.description || "",
      qualifications: t.qualifications || "",
    });
    setTplFlash({ kind: "ok", msg: `Loaded template "${t.name}". Edit fields as needed.` });
  };

  const saveAsTemplate = async () => {
    const name = tplName.trim();
    if (!name) { setTplFlash({ kind: "error", msg: "Template name is required." }); return; }
    setTplBusy(true); setTplFlash(null);
    try {
      const payload = {
        name,
        title: form.title.trim(),
        department: form.department,
        location: form.location,
        hiringManager: form.hiringManager,
        openings: parseInt(form.openings, 10) || 1,
        description: form.description,
        qualifications: form.qualifications,
      };
      await http.post("/requisition-templates", payload);
      setTplFlash({ kind: "ok", msg: `Template "${name}" saved.` });
      setTplName("");
      setShowSaveTpl(false);
      await loadTemplates();
    } catch (e) {
      setTplFlash({ kind: "error", msg: e.response?.data?.detail || e.message });
    } finally { setTplBusy(false); }
  };

  const deleteTemplate = async () => {
    if (!selectedTemplateId) return;
    const t = templates.find((x) => x.id === selectedTemplateId);
    if (!t) return;
    if (!window.confirm(`Delete template "${t.name}"? This cannot be undone.`)) return;
    setTplBusy(true); setTplFlash(null);
    try {
      await http.delete(`/requisition-templates/${selectedTemplateId}`);
      setTplFlash({ kind: "ok", msg: `Template "${t.name}" deleted.` });
      setSelectedTemplateId("");
      await loadTemplates();
    } catch (e) {
      setTplFlash({ kind: "error", msg: e.response?.data?.detail || e.message });
    } finally { setTplBusy(false); }
  };

  const submit = async () => {
    setErr(null);
    if (!form.title.trim()) { setErr("Title is required."); return; }
    setBusy(true);
    try {
      const payload = {
        ...form,
        title: form.title.trim(),
        openings: parseInt(form.openings, 10) || 1,
      };
      if (isEdit) {
        await http.patch(`/requisitions/${initial.id}`, payload);
      } else {
        await http.post("/requisitions", payload);
      }
      onSaved();
    } catch (e) {
      setErr(e.response?.data?.detail || e.message);
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[60] grid place-items-center bg-black/50" data-testid="requisition-modal">
      <div className="card w-full max-w-2xl p-6 max-h-[92vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="font-display text-xl font-bold">{isEdit ? "Edit Draft Requisition" : "New Requisition"}</h2>
            <div className="text-xs text-slate-500 mt-0.5">Requisitions are stored in Vaku and used across AI Interviews, Bulk CV Eval and candidate flows.</div>
          </div>
          <button onClick={onClose} className="btn-ghost p-1"><X size={18} /></button>
        </div>

        {/* Template picker — only shown on Create (not on Edit) */}
        {!isEdit && (
          <div
            className="mb-4 rounded-md border border-slate-200 bg-slate-50/60 p-3"
            data-testid="req-template-picker"
          >
            <div className="flex items-center gap-2 mb-2">
              <BookmarkSimple size={14} weight="fill" className="text-indigo-600" />
              <span className="label-eyebrow text-slate-600">Requisition Templates</span>
            </div>
            <div className="flex flex-wrap items-end gap-2">
              <div className="flex-1 min-w-[220px]">
                <label className="text-[11px] text-slate-500 block mb-1">Load from template</label>
                <select
                  data-testid="req-template-select"
                  className="input"
                  value={selectedTemplateId}
                  onChange={(e) => applyTemplate(e.target.value)}
                >
                  <option value="">— Start from scratch —</option>
                  {templates.map((t) => (
                    <option key={t.id} value={t.id}>{t.name}</option>
                  ))}
                </select>
              </div>
              {selectedTemplateId && (
                <button
                  type="button"
                  data-testid="req-template-delete"
                  className="btn btn-outline !py-1.5 !px-2 !text-xs text-red-700 border-red-300 hover:bg-red-50"
                  disabled={tplBusy}
                  onClick={deleteTemplate}
                  title="Delete this template"
                >
                  <Trash size={13} /> Delete
                </button>
              )}
              <button
                type="button"
                data-testid="req-template-save-open"
                className="btn btn-outline !py-1.5 !px-2 !text-xs"
                onClick={() => setShowSaveTpl((v) => !v)}
              >
                <FloppyDisk size={13} /> {showSaveTpl ? "Cancel" : "Save current as template"}
              </button>
            </div>
            {showSaveTpl && (
              <div className="mt-2 flex flex-wrap items-end gap-2">
                <div className="flex-1 min-w-[220px]">
                  <label className="text-[11px] text-slate-500 block mb-1">Template name</label>
                  <input
                    data-testid="req-template-name-input"
                    className="input"
                    placeholder="e.g. Payroll Analyst KSA"
                    value={tplName}
                    onChange={(e) => setTplName(e.target.value)}
                  />
                </div>
                <button
                  type="button"
                  data-testid="req-template-save-confirm"
                  className="btn btn-primary !py-1.5 !px-3 !text-xs"
                  disabled={tplBusy || !tplName.trim()}
                  onClick={saveAsTemplate}
                >
                  {tplBusy ? "Saving…" : "Save template"}
                </button>
              </div>
            )}
            {tplFlash && (
              <div
                data-testid="req-template-flash"
                className={`mt-2 text-xs rounded px-2 py-1 border ${tplFlash.kind === "error" ? "border-red-300 bg-red-50 text-red-800" : "border-emerald-300 bg-emerald-50 text-emerald-800"}`}
              >
                {tplFlash.msg}
              </div>
            )}
          </div>
        )}

        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2">
            <label className="label-eyebrow block mb-1">Job Title *</label>
            <input
              data-testid="req-title"
              className="input"
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              placeholder="e.g. Senior Payroll Analyst"
            />
          </div>
          <div>
            <label className="label-eyebrow block mb-1">Department</label>
            <input data-testid="req-department" className="input" value={form.department} onChange={(e) => setForm({ ...form, department: e.target.value })} />
          </div>
          <div>
            <label className="label-eyebrow block mb-1">Location</label>
            <select data-testid="req-location" className="input" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })}>
              {["Riyadh", "Jeddah", "Dammam", "Remote"].map((l) => <option key={l}>{l}</option>)}
            </select>
          </div>
          <div>
            <label className="label-eyebrow block mb-1">Hiring Manager</label>
            <input data-testid="req-hm" className="input" value={form.hiringManager} onChange={(e) => setForm({ ...form, hiringManager: e.target.value })} />
          </div>
          <div>
            <label className="label-eyebrow block mb-1">Openings</label>
            <input
              data-testid="req-openings"
              type="number"
              min={1}
              className="input"
              value={form.openings}
              onChange={(e) => setForm({ ...form, openings: e.target.value })}
            />
          </div>
          <div className="col-span-2">
            <label className="label-eyebrow block mb-1">Job Description</label>
            <textarea
              data-testid="req-description"
              className="input"
              rows={5}
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              placeholder="Role summary, responsibilities, team, reporting line…"
            />
          </div>
          <div className="col-span-2">
            <label className="label-eyebrow block mb-1">Qualifications</label>
            <textarea
              data-testid="req-qualifications"
              className="input"
              rows={3}
              value={form.qualifications}
              onChange={(e) => setForm({ ...form, qualifications: e.target.value })}
              placeholder="Required skills, certifications, years of experience…"
            />
          </div>
        </div>
        {err && (
          <div className="mt-3 rounded-md border border-red-300 bg-red-50 p-3 text-sm text-red-800" data-testid="req-form-error">
            {err}
          </div>
        )}
        <div className="mt-5 flex justify-end gap-2">
          <button className="btn btn-outline" onClick={onClose}>Cancel</button>
          <button
            data-testid="req-save"
            className="btn btn-primary"
            disabled={busy}
            onClick={submit}
          >
            {busy ? "Saving…" : isEdit ? "Save Draft" : "Create Draft"}
          </button>
        </div>
      </div>
    </div>
  );
}
